import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Box,
  Avatar,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  createTheme,
  ThemeProvider,
  CssBaseline,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import AddIcon from '@mui/icons-material/Add';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import MicOutlinedIcon from '@mui/icons-material/MicOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { get, post } from '../../utility/fetchWeb';
import { get as gets } from '../../utility/fetch';

import { useNavigate } from 'react-router-dom';
import { useDrawer } from '../../contexts/DrawerContext';
import ChatVideoCall from '../Layout/ChatVideoCall';
import { VideoCall } from '../Layout/VideoCall';
import { useVideoCall } from '../../contexts/VideoCallContext';
import VideoCalling from '../Layout/VideoCalling';
import AudioCall from '../Layout/AudioCall';

const doctorAvatar = 'https://i.imgur.com/example-dr-ember.jpg';

const theme = createTheme({
  palette: {
    primary: { main: '#28a745' },
    secondary: { main: '#e0e0e0' },
    background: { default: '#f8f9fa', paper: '#fff' },
    text: { primary: '#333', secondary: '#6c757d' },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h6: { fontWeight: 600, fontSize: '1.2rem', '@media (max-width:600px)': { fontSize: '1rem' } },
    body1: { fontSize: '0.95rem' },
    body2: { fontSize: '0.85rem', color: '#6c757d' },
    caption: { fontSize: '0.75rem', color: '#6c757d' },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          color: '#333',
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0',
        },
      },
    },
    MuiAvatar: { styleOverrides: { root: { width: 40, height: 40 } } },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '25px',
            backgroundColor: '#eafcec',
            paddingRight: '8px',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: '#cce5d6' },
            '&.Mui-focused fieldset': { borderColor: '#28a745' },
          },
        },
      },
    },
    MuiIconButton: { styleOverrides: { root: { color: '#6c757d' } } },
  },
});

function Chat({ id, sender, receiver }) {
  const messagesEndRef = useRef(null);
  const [messageInput, setMessageInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [receiverPatient, setReceiverPatient] = useState({});
  const [receiverDoctor, setReceiverDoctor] = useState({});
  const [messageFile, setMessageFile] = useState(null);
  // const [showVideoChatModal, setShowVideoChatModal] = useState();
  // const [audioChatModal, setShowAudioChatModal] = useState();
  const { chatId } = useDrawer();
  const navigate = useNavigate();
  const query = new URLSearchParams(window.location.search);

  const [showVideoChatModal, setShowVideoChatModal] = useState(false);
  const [startAudioOnly, setStartAudioOnly] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [appId, setAppId] = useState(process.env.REACT_APP_AGORA_KEY);
  const [channel, setChannel] = useState("call");
  const [token, setToken] = useState(null);
  const [uid, setUid] = useState(Number(sessionStorage.getItem("userId")) || "defaultUid");
  const [showAudioChatModal, setShowAudioChatModal] = useState(false);
  const [callId, setCallId] = useState(null); // Add callId state

  const { startCall } = useVideoCall();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };


  useEffect(() => {
    if (messages.length && !hasScrolledToBottom) {
      scrollToBottom();
      setHasScrolledToBottom(true);
    }
  }, [messages, hasScrolledToBottom]);

  const getReceiverPatient = async (targetId) => {
    try {
      const res = await get(`/patient/single/${targetId}`);
      if (res?.status === 'success') setReceiverPatient(res?.data || {});
    } catch (error) {
      console.error("Failed to fetch patient:", error);
      setReceiverPatient({});
    }
  };

  const getReceiverDoctor = async (targetId) => {
    try {
      const res = await gets(`/employee/employee/${targetId}`);
      if (res) setReceiverDoctor(res?.data || {});
    } catch (error) {
      console.error("Failed to fetch doctor:", error);
      setReceiverDoctor({});
    }
  };

  const getChats = async () => {
    try {
      const res = await get(
        `/Posts/conversation-between-users?user1Id=${sessionStorage?.getItem("userId") || sessionStorage.getItem("userID")}&user1Type=${sender || query?.get("s")}&user2Id=${query?.get("rrt") || chatId}&user2Type=${receiver || query?.get("r")}&pageNumber=1&pageSize=20`
      );
      setMessages(res?.chat || []);

    } catch (error) {
      console.error("Failed to fetch chats:", error);
      setMessages([]);
    }
  };
  useEffect(() => {
    getChats();

    const rType = Number(receiver ?? query?.get("r"));
    const targetId = query?.get("rrt") || chatId;

    if (rType === 1) {
      getReceiverPatient(targetId);
    } else if (rType === 2) {
      getReceiverDoctor(targetId);
    } else {
      console.warn('Unsupported receiver type, closing chat:', rType);
      navigate(-1);
      return; // do not open chat
    }

    const interval = setInterval(() => getChats(), 4000);
    return () => clearInterval(interval);
  }, [chatId]);

  // useEffect(() => scrollToBottom(), [messages]);

  const handleSendMessage = async () => {
    let res;
    try {
      if (messageFile) {
        const formData = new FormData();
        formData.append('image', messageFile);
        formData.append("senderId", Number(sessionStorage?.getItem("userId") || Number(sessionStorage?.getItem("userID"))))
        formData.append("senderType", Number(query?.get("s")))
        formData.append("receiverId", Number(query?.get("rrt")) || chatId)
        formData.append("receiverType", Number(query?.get("r")))
        formData.append("text", messageInput)

        res = await post('/Posts/send-text-with-image', formData);
        setMessageInput(''); // Clear input immediately
        if (res?.status === 'success') {
          setMessageFile(null);
          getChats();
        }
      } else {
        if (!messageInput.trim()) return;
        setMessageInput(''); // Clear input immediately
        res = await post(`/Posts/send-text`, {
          senderId: Number(sessionStorage?.getItem("userId")) || Number(sessionStorage?.getItem("userID")),
          senderType: Number(query?.get("s")),
          receiverId: Number(query?.get("rrt")) || chatId,
          receiverType: Number(query?.get("r")),
          text: messageInput,
        });
        getChats();
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  // let getAgoraDetails = async (receiverId) => {
  //   let res = await get(`/posts/generate-agora-token?channelName=${channel}&uid=${uid}&receiverId=${Number(sessionStorage.getItem("userId"))}`);

  //   console.log(res);

  //   if (res && res.token) {
  //     setToken(res.token);
  //     setAppId(res.appId || appId);
  //     setChannel(res.channel || channel);
  //     setUid(res.uid || uid);

  //     notifyCaller(receiverId);

  //   } else {
  //     console.error("Failed to fetch token");
  //   }
  // }


  const notifyCaller = async (chann) => {
    try {
      const res = await post(`/Posts/notify-call`, {
        receiverId: Number(query?.get("rrt")) || chatId,
        channelName: chann,
        callerId: Number(sessionStorage.getItem("userId")),
        callerName: sessionStorage.getItem("name"),
        userType: Number(query?.get("r"))
      });
      console.log(res)
      if (res?.data) {
        console.log("Caller notified successfully");
      } else {
        console.error("Failed to notify caller:", res);
      }
    } catch (error) {
      console.error("Error notifying caller:", error);
    }
  }


  // const handleVideoChat = async () => {
  //   await getAgoraDetails();
  //   setShowVideoChatModal(true);
  // }

  // const handleAudioChat = () => {
  //   setShowVideoChatModal(true);
  // }

  // const handleCloseVideoChat = () => {
  //   setShowVideoChatModal(false);
  //   // Force cleanup of any lingering tracks
  //   if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
  //     navigator.mediaDevices.getUserMedia({ video: true, audio: true })
  //       .then(stream => {
  //         stream.getTracks().forEach(track => track.stop());
  //       })
  //       .catch(err => console.error('Media cleanup error:', err));
  //   }
  // }



  const handleFileUpload = async (e) => {
    try {
      const file = e.target.files[0];
      setMessageFile(file);
      // If you want to upload immediately, uncomment and wrap in try/catch as well
      // if (file) {
      //   const formData = new FormData();
      //   formData.append('file', file);
      //   await post('/chat/upload', formData);
      //   getChats();
      // }
    } catch (error) {
      console.error("Failed to upload file:", error);
    }
  };

  const startSpeechToText = () => {
    if (!('webkitSpeechRecognition' in window)) return alert('Speech recognition not supported');
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setMessageInput((prev) => prev + ' ' + transcript);
    };
    recognition.start();
  };


  // ... your existing state and functions (getChats, getReceiver, etc.)

  const getAgoraDetails = async () => {
    // This function can be simplified, as you fetch details right before the call
    // Ensure you have a valid channel name and UID before calling
    try {
      const res = await get(`/posts/generate-new-token?channelName=${`connecthealthpro_videocall-${uid}&${query.get("rrt")}`}&uid=${uid}`);
      if (res && res.token) {
        setToken(res.token);
        setUid(Number(res.uid) || uid);
        // Optional: Update state if the server provides different values
        setAppId(res.appId || process.env.REACT_APP_AGORA_KEY);
        setChannel(res.channelName || "call");

        // startCall({
        //   appId: res.appId,
        //   channel: `connecthealthpro_videocall-${uid}&${query.get("rrt")}`,
        //   token: res.token,
        //   uid: Number(sessionStorage.getItem("userId")) ,
        // });

        return true; // Indicate success
      } else {
        console.error("Failed to fetch Agora token");
        return false;
      }
    } catch (error) {
      console.error("Error fetching Agora token:", error);
      return false;
    }
  };

  
  


  const handleVideoChat = async () => {
    const success = await getAgoraDetails();
    if (success) {
      setStartAudioOnly(false);
      setCallId(Date.now()); // Generate unique callId
      setShowVideoChatModal(true);
      notifyCaller(`${`connecthealthpro_videocall-${uid}&${query.get("rrt")}`}`);
    }
  };

  const handleAudioChat = async () => {
    const success = await getAgoraDetails();
    if (success) {
      setStartAudioOnly(true);
      setCallId(Date.now()); // Generate unique callId
      setShowAudioChatModal(true);
      notifyCaller(`${`connecthealthpro_audiocall-${uid}&${query.get("rrt")}`}`);
    }
  };


  // 3. SIMPLIFY the close handler. No manual cleanup needed!
  const handleCloseVideoChat = () => {
    setShowVideoChatModal(false);
    setToken(null);
    setCallId(null); // Reset callId
  };

  const renderMessage = (message, index) => {
    const currentUserId = sessionStorage?.getItem("userId");
    const isCurrentUser = message?.senderId?.toString() === currentUserId;
    return (
      <Box key={index} sx={{ display: 'flex', justifyContent: isCurrentUser ? 'flex-end' : 'flex-start', mb: 1.5 }}>
        <Box
          sx={{
            maxWidth: '75%',
            backgroundColor: isCurrentUser ? theme.palette.primary.main : theme.palette.grey[300],
            color: isCurrentUser ? '#fff' : theme.palette.text.primary,
            borderRadius: '12px',
            p: 1.5,
            wordBreak: 'break-word',
          }}
        >
          <img src={process.env.REACT_APP_DOC_URL + message?.imageUrl} alt="" srcset="" />
          <Typography variant="body1">{message.text}</Typography>
          <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
            {new Date(message?.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            {isCurrentUser && message.isRead && <CheckCircleIcon sx={{ fontSize: '0.8rem', color: '#fff', ml: 0.5 }} />}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="sm" sx={{ py: 2, display: 'flex', flexDirection: 'column', height: '90vh' }}>
        <Box sx={{ flexShrink: 0 }}>
          <AppBar position="static">
            <Toolbar>
              <IconButton onClick={() => navigate(-1)}>
                <ArrowBackIcon />
              </IconButton>
              <Avatar src={receiverDoctor?.profilePicture || receiverPatient?.profilePicture || doctorAvatar} sx={{ mx: 1 }} />
              <Box>
                <Typography variant="h6">
                  {receiverDoctor?.title || ''} {receiverPatient?.firstName || receiverDoctor?.firstName}{' '}
                  {receiverPatient?.lastName || receiverDoctor?.lastName}
                </Typography>
                <Typography variant="body2">
                  {receiverDoctor?.department || receiverDoctor?.workGrade || ''}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton onClick={handleVideoChat}><VideocamOutlinedIcon /></IconButton>
              <IconButton onClick={handleAudioChat}><PhoneOutlinedIcon /></IconButton>
            </Toolbar>
          </AppBar>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
          {messages.map(renderMessage)}
          <div ref={messagesEndRef} />
        </Box>

        <Box sx={{ flexShrink: 0, p: 2, backgroundColor: theme.palette.background.default }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton component="label">
              <CameraAltOutlinedIcon />
              <input hidden accept="image/*" type="file" onChange={handleFileUpload} />
            </IconButton>
            <TextField
              variant="outlined"
              placeholder="Type a message..."
              fullWidth
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={startSpeechToText}><MicOutlinedIcon /></IconButton>
                    <IconButton disabled={!messageInput.trim()} onClick={handleSendMessage}><SendIcon /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Box>
        {
          showVideoChatModal &&
          <VideoCalling
            appId={appId}
            channel={channel}
            token={token}
            uid={uid}
            callId={callId} // Pass callId
            open={showVideoChatModal}
            onClose={handleCloseVideoChat}
            startWithVideoOff={startAudioOnly}
            isAudio={startAudioOnly ? true : false}
          />

        }

        {/* {
          showAudioChatModal &&
          <AudioCall
            appId={appId}
            channel={channel}
            token={token}
            uid={uid}
            open={showVideoChatModal}
            onClose={handleCloseVideoChat}
            startWithVideoOff={startAudioOnly}
            mode={startAudioOnly ? 'audio' : 'video'}
          />

        } */}
      </Container>
    </ThemeProvider>
  );
}

export default Chat;